import React from 'react';
import { useApolloClient } from '@apollo/client';
import type { ActiveFilter } from '@oysterjs/ui/search-filter';
import { getSinceDate } from '~/api/data/filter-base';
import {
  GetTriggerExecutionHistoryDocument,
  type GetTriggerExecutionHistoryQuery,
  type GetTriggerExecutionHistoryQueryVariables,
  type TriggerExecutionHistoryFilter,
  TriggerEventState,
  TriggerEventType
} from '~/api/graphql';
import type { TriggerExecutionFilter } from './trigger-filters';

export type HistoryEdge =
  GetTriggerExecutionHistoryQuery['triggerExecutionHistory']['edges'][number];

// Preserve local-midnight/date-duration semantics. Each first-page read gets a
// fresh cutoff; continuation uses the cutoff of the last successful first page.
export const historyFilter = (
  filters: ActiveFilter<TriggerExecutionFilter>[]
): TriggerExecutionHistoryFilter => ({
  search: filters.filter((f) => f.type === 'search').map((f) => f.value),
  state: filters.find((f) => f.type === 'state')?.value as TriggerEventState | undefined,
  type: filters.find((f) => f.type === 'type')?.value as TriggerEventType | undefined,
  since: getSinceDate(filters.find((f) => f.type === 'date')?.value ?? '')
});

export const historyIdentity = (edge: HistoryEdge) =>
  edge.event ? `event:${edge.event.id}` : `email:${edge.emailRun!.id}`;

type State = {
  scope: string;
  edges: HistoryEdge[];
  cursor?: string | null;
  more: boolean;
  loading: boolean;
  error: boolean;
  paused: boolean;
};

type Job = { owner: object; run: () => Promise<void> };
type Queue = { busy: boolean; pending?: Job };

// One transport at a time, including across scope changes. Only the latest
// queued intent survives; an obsolete, uncancellable query must settle first.
const drain = (queue: Queue) => {
  if (queue.busy || !queue.pending) return;
  const job = queue.pending;
  queue.pending = undefined;
  queue.busy = true;
  void job.run().finally(() => {
    queue.busy = false;
    drain(queue);
  });
};

type Request = {
  kind: 'first' | 'poll' | 'more';
  after?: string | null;
  filter: TriggerExecutionHistoryFilter;
};

/** Poll only the latest first page. Starting older-page browsing freezes that
 * window until deliberate Refresh or a scope change. Never splice a new head
 * onto old cursors, and never apply partial combined-source results. */
export const useExecutionHistory = (
  tenant: string,
  filters: ActiveFilter<TriggerExecutionFilter>[],
  viewer = ''
) => {
  const client = useApolloClient();
  const [revision, setRevision] = React.useState(0);
  const filterKey = JSON.stringify(filters.map(({ type, value }) => ({ type, value })));
  const scope = JSON.stringify([tenant, viewer, filterKey, revision]);
  const queue = React.useRef<Queue>({ busy: false });
  const actions = React.useRef({ loadMore: () => {}, retry: () => {} });
  const [state, setState] = React.useState<State>({
    scope: '',
    edges: [],
    more: false,
    loading: true,
    error: false,
    paused: false
  });

  React.useEffect(() => {
    const owner = {};
    let active = true;
    let epoch = 0;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let committedFilter = historyFilter(filters);
    let failed: Request | undefined;
    let view: State = {
      scope,
      edges: [],
      more: false,
      loading: !!tenant,
      error: false,
      paused: false
    };
    const publish = (next: State) => {
      view = next;
      setState(next);
    };
    const cancelTimer = () => {
      clearTimeout(timer);
      timer = undefined;
    };
    const start = (request: Request) => {
      if (!active || !tenant) return;
      cancelTimer();
      const current = ++epoch;
      if (request.kind !== 'poll') publish({ ...view, loading: true, error: false });
      queue.current.pending = {
        owner,
        run: async () => {
          if (!active || current !== epoch) return;
          try {
            const result = await client.query<
              GetTriggerExecutionHistoryQuery,
              GetTriggerExecutionHistoryQueryVariables
            >({
              query: GetTriggerExecutionHistoryDocument,
              variables: { first: 50, after: request.after, filter: request.filter },
              fetchPolicy: 'no-cache',
              errorPolicy: 'none'
            });
            if (result.errors?.length || !result.data?.triggerExecutionHistory)
              throw new Error('incomplete');
            if (!active || current !== epoch) return;
            const page = result.data.triggerExecutionHistory;
            const edges = request.kind === 'more' ? [...view.edges] : [];
            const seen = new Set(edges.map(historyIdentity));
            for (const edge of page.edges) {
              const id = historyIdentity(edge);
              if (!seen.has(id)) {
                seen.add(id);
                edges.push(edge);
              }
            }
            committedFilter = request.filter;
            failed = undefined;
            publish({
              ...view,
              edges,
              cursor: page.pageInfo.endCursor,
              more: page.pageInfo.hasNextPage,
              loading: false,
              error: false
            });
          } catch {
            if (!active || current !== epoch) return;
            failed = request;
            publish({ ...view, loading: false, error: true });
          } finally {
            // Completion-based delay: no overlap, catch-up loop or queued ticks.
            if (active && current === epoch && !view.paused) {
              timer = setTimeout(
                () => start({ kind: 'poll', filter: historyFilter(filters) }),
                5000
              );
            }
          }
        }
      };
      drain(queue.current);
    };
    const retry = () => {
      if (!view.loading && failed)
        start({ ...failed, kind: failed.kind === 'poll' ? 'first' : failed.kind });
    };
    actions.current = {
      retry,
      loadMore: () => {
        if (!active || view.loading) return;
        if (!view.more) {
          if (view.error) retry();
          return;
        }
        // Invalidate an in-flight poll before it can replace the displayed
        // boundary; the queued continuation uses only that displayed page.
        publish({ ...view, paused: true });
        start({ kind: 'more', after: view.cursor, filter: committedFilter });
      }
    };
    publish(view);
    start({ kind: 'first', filter: committedFilter });
    return () => {
      active = false;
      ++epoch;
      cancelTimer();
      if (queue.current.pending?.owner === owner) queue.current.pending = undefined;
    };
  }, [client, scope]);

  const visible =
    state.scope === scope
      ? state
      : {
          scope,
          edges: [],
          more: false,
          loading: !!tenant,
          error: false,
          paused: false
        };
  return {
    ...visible,
    loadMore: () => actions.current.loadMore(),
    retry: () => actions.current.retry(),
    refresh: () => setRevision((v) => v + 1)
  };
};
